import { v4 as uuidv4 } from 'uuid';
import { createWebStorage } from './storage';
import { decodeJWT } from './jwt';

const resolveValue = (valueOrFn) => (typeof valueOrFn === 'function' ? valueOrFn() : valueOrFn);
const ABSOLUTE_HTTP_URL_REGEX = /^https?:\/\//i;

// Session codes returned by /api/check-session that require re-authentication:
//   LEGACY  — unmigrated user, needs first-time login through SSO
//   CHANGED — known user, SSO cookie rotated, needs re-login
// Both carry status: 'invalid'. Frontend switches on code alone.
export const SESSION_CODES_REQUIRING_SSO = new Set(['CHANGED', 'LEGACY']);

// localStorage key that persists after the first LEGACY redirect so we can
// distinguish a brand-new (first time) from a repeat attempt.
const LEGACY_SEEN_KEY = 'legacy_login_seen';

const resolveCheckSessionAuthFromCode = (code) => code === 'ACTIVE';

const normalizeCheckSessionResult = (data = {}, fallbackState = '') => {
  const code = String(data?.code || '').toUpperCase();
  return {
    ...data,
    code,
    authenticated: resolveCheckSessionAuthFromCode(code),
    state: data?.state || fallbackState
  };
};

const defaultBuildPostLoginRedirect = (base, returnUrl) => {
  if (!returnUrl) return base || '';
  if (!base) return returnUrl;
  return `${base}${returnUrl}`;
};

/**
 * Creates an auth client. Pure (no React). One instance per app.
 *
 * Options:
 *   authBaseUrl, postLoginRedirectUri  - string | () => string
 *   http                               - axios-like instance (required)
 *   storage                            - storage adapter (default: createWebStorage())
 *   buildPostLoginRedirect(base, ret)  - customize PLRU construction
 */
export const createAuthClient = ({
  authBaseUrl,
  postLoginRedirectUri,
  http,
  storage = createWebStorage(),
  buildPostLoginRedirect = defaultBuildPostLoginRedirect
} = {}) => {
  if (!http) {
    throw new Error('createAuthClient requires an axios-like http client');
  }

  const getAuthBaseUrl = () => resolveValue(authBaseUrl) || '';
  const getPostLoginRedirectUri = () => resolveValue(postLoginRedirectUri) || '';

  // ── Sync accessors ─────────────────────────────────────────────────────────
  const getState = () => storage.getItem('state') || '';
  const getEncodedJWT = () => storage.getItem('id_token');
  const getStoredIdToken = () => storage.getItem('id_token');

  const getJWT = () => {
    const idToken = storage.getItem('id_token');
    const decoded = idToken ? decodeJWT(idToken) : null;
    return decoded?.payload || null;
  };

  const getJWTSub = () => {
    const payload = getJWT();
    const sub = payload?.sub || '';
    const matches = sub.match(/user\/([^/]+)\/([^/]+)/);
    if (!matches) return { aoid: undefined, ooid: undefined };
    return { ooid: matches[1], aoid: matches[2] };
  };

  // Single source of truth for "is the user authenticated?" — token presence.
  const getAuthenticated = () => !!storage.getItem('id_token');

  const clearAuth = () => storage.clearAuth();

  // ── Network calls ──────────────────────────────────────────────────────────
  const login = async (postLoginRedirectUriOverride = '', returnUrl = '') => {
    clearAuth();
    const state = uuidv4();
    storage.setItem('state', state);

    const base = postLoginRedirectUriOverride || getPostLoginRedirectUri();
    const fullRedirect = buildPostLoginRedirect(base, returnUrl);
    console.debug('[auth] login redirect', { base, returnUrl, fullRedirect });
    const qs = [
      `state=${encodeURIComponent(state)}`,
      `post_login_redirect_uri=${encodeURIComponent(fullRedirect)}`
    ].join('&');
    window.location.replace(`${getAuthBaseUrl()}/api/authenticate?${qs}`);
  };

  const logout = async (redirectPath = '') => {
    const state = storage.getItem('state') || '';
    clearAuth();

    const postLogoutRedirectUri = redirectPath
      ? ABSOLUTE_HTTP_URL_REGEX.test(redirectPath)
        ? redirectPath
        : `${getPostLoginRedirectUri()}#/${redirectPath}`
      : getPostLoginRedirectUri();

    const qs = [
      `state=${encodeURIComponent(state)}`,
      `post_logout_redirect_uri=${encodeURIComponent(postLogoutRedirectUri)}`
    ].join('&');
    window.location.replace(`${getAuthBaseUrl()}/api/logout?${qs}`);
  };

  const getIdToken = async () => {
    const state = storage.getItem('state');
    if (!state) return null;
    try {
      const response = await http.get(`/api/id-token?state=${state}`, {
        baseURL: getAuthBaseUrl(),
        withCredentials: true
      });
      const token = response?.data?.id_token;
      if (token) {
        storage.setItem('id_token', token);
        return token;
      }
      return null;
    } catch {
      return null;
    }
  };

  const authStatus = async () => {
    try {
      const response = await http.get('/api/auth-status', {
        baseURL: getAuthBaseUrl(),
        withCredentials: true
      });
      const data = response?.data;
      if (data?.authenticated && data?.state) {
        storage.setItem('state', data.state);
        storage.setItem('authenticated', !!data.authenticated);
        if (data.expires_at) storage.setItem('expires_at', data.expires_at);
        if (data.id_token) storage.setItem('id_token', data.id_token);
        return data;
      }
      clearAuth();
      return data || { authenticated: false };
    } catch {
      return { authenticated: false };
    }
  };

  const checkSession = async (preferredState = '') => {
    const state = preferredState || getState();
    try {
      console.log('[check-session] request:', `${getAuthBaseUrl()}/api/check-session`, { state: state || '' });
      const response = await http.get('/api/check-session', {
        baseURL: getAuthBaseUrl(),
        withCredentials: true,
        params: { state: state || '' }
      });
      console.log('[check-session] response:', response.status, response.data);
      const result = normalizeCheckSessionResult(response.data, state);
      if (result.authenticated) {
        storage.setItem('state', result.state);
        storage.setItem('authenticated', true);
        if (result.expires_at) storage.setItem('expires_at', result.expires_at);
        if (result.id_token) storage.setItem('id_token', result.id_token);
        return result;
      }
      if (result.code === 'LEGACY') {
        // LEGACY_SEEN_KEY is never written anymore; clear any stale value left
        // by old code so it cannot suppress the SSO redirect.
        storage.removeItem(LEGACY_SEEN_KEY);
        clearAuth();
        return { ...result, isFirstLegacy: true };
      }
      clearAuth();
      return result;
    } catch (error) {
      const data = error?.response?.data;
      console.log('[check-session] error:', error?.response?.status, data);
      clearAuth();
      if (data) return normalizeCheckSessionResult(data, state);
      return { authenticated: false, code: 'NETWORK_ERROR', state };
    }
  };

  const requiresSso = (code) => SESSION_CODES_REQUIRING_SSO.has(String(code || '').toUpperCase());

  // Reset the LEGACY seen flag 
  const clearLegacySeen = () => storage.removeItem(LEGACY_SEEN_KEY);

  // ── Memoized session bootstrap ─────────────────────────────────────────────
  // Multiple components/hooks can call ensureSession concurrently (React
  // StrictMode double-invokes effects); we want exactly one network round
  // trip per page load. The in-flight promise is shared by all callers and
  // released when settled so a later explicit re-check is still possible.
  let inflight = null;
  const ensureSession = () => {
    if (inflight) return inflight;
    inflight = (async () => {
      const session = await checkSession();
      let idToken = session?.id_token || getStoredIdToken();
      if (session.authenticated && !idToken) {
        idToken = await getIdToken();
      }
      return {
        authenticated: !!session.authenticated,
        code: session.code,
        state: session.state,
        isFirstLegacy: session.isFirstLegacy ?? null,
        idToken: idToken || null,
        raw: session
      };
    })().finally(() => {
      inflight = null;
    });
    return inflight;
  };

  return {
    // sync
    getAuthenticated,
    getState,
    getJWT,
    getEncodedJWT,
    getStoredIdToken,
    getJWTSub,
    clearAuth,
    requiresSso,
    clearLegacySeen,
    // async
    login,
    logout,
    getIdToken,
    authStatus,
    checkSession,
    ensureSession
  };
};
